import { create } from 'zustand';

interface SubcategoryDataRecord {
  weekId: number;
  value: number;
}

interface SubcategoryData {
  records: SubcategoryDataRecord[];
  timestamp: number; // To track when data was cached
}

interface SubcategoryDataStore {
  cache: Map<string, SubcategoryData>;
  getSubcategoryData: (key: string) => SubcategoryData | undefined;
  setSubcategoryData: (key: string, data: SubcategoryData) => void;
  clearCache: () => void;
  // Helper to check if cached data is still valid (within 5 minutes)
  isDataFresh: (key: string) => boolean;
}

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export const useMicroTrendSubcategoryDataStore = create<SubcategoryDataStore>((set, get) => ({
  cache: new Map(),

  getSubcategoryData: (key: string) => {
    return get().cache.get(key);
  },

  setSubcategoryData: (key: string, data: SubcategoryData) => {
    set((state) => {
      const newCache = new Map(state.cache);
      newCache.set(key, {
        ...data,
        timestamp: Date.now()
      });
      return { cache: newCache };
    });
  },

  isDataFresh: (key: string) => {
    const data = get().cache.get(key);
    if (!data) return false;
    return Date.now() - data.timestamp < CACHE_DURATION_MS;
  },

  clearCache: () => {
    set({ cache: new Map() });
  }
}));

/**
 * Generates a unique cache key for subcategory data
 */
export const generateSubcategoryCacheKey = (subcategoryId: number, startWeekId: number, endWeekId: number): string => {
  return `subcategory-${subcategoryId}-${startWeekId}-${endWeekId}`;
};
